import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import type { z } from "zod"
import {
  productCostCoverageResponseSchema,
  productCostHistoryQuerySchema,
  productCostHistoryResponseSchema,
  productCostIdentitiesResponseSchema,
  routes,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

function validateCostResponse<Data>(schema: z.ZodType<Data>, value: unknown): Data {
  const result = schema.safeParse(value)
  if (!result.success) {
    throw new Error("Cost history returned an invalid response. Please retry.")
  }
  return result.data
}

export function useProductCostIdentities(sku: string) {
  return useQuery({
    queryKey: queryKeys.productCostIdentities(sku),
    queryFn: async ({ signal }) => {
      const result = await apiFetch<unknown>(routes.products.costIdentities({ sku }), { signal })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return validateCostResponse(productCostIdentitiesResponseSchema, result.value)
    },
  })
}

export function useProductCostCoverage() {
  return useQuery({
    queryKey: queryKeys.productCostCoverage,
    queryFn: async ({ signal }) => {
      const result = await apiFetch<unknown>(routes.products.costCoverage(), { signal })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return validateCostResponse(productCostCoverageResponseSchema, result.value)
    },
  })
}

export function useProductCostHistory(sku: string, store: string, from?: string, to?: string) {
  const filter = productCostHistoryQuerySchema.safeParse({ store, from, to })
  return useInfiniteQuery({
    queryKey: queryKeys.productCostHistory(sku, store, from, to),
    enabled: sku.length > 0 && filter.success,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<unknown>(routes.products.costHistory({ sku }), {
        signal,
        query: {
          store,
          limit: "30",
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return validateCostResponse(productCostHistoryResponseSchema, result.value)
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}
